import { type FC, useLayoutEffect, useRef, useState } from "react";

import type { OutgoingIntakeReminderSchedule } from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import {
  CustomReminders,
  ReminderPresetSelect,
  useReminderCadence
} from "../editor/ReminderCadence";
import { EXPIRY_DAYS } from "../editor/reminders";
import { isEmail, type IntakeDraft } from "../editor/useIntakeDraft";
import type { IntakeWelcomeVideo } from "../editor/welcomeVideo/useIntakeWelcomeVideo";
import { WelcomeVideoCard } from "../editor/welcomeVideo/WelcomeVideoCard";
import { type EmailPreviewState, useEmailPreview } from "./useEmailPreview";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: IntakeDraft;
  video: IntakeWelcomeVideo;
  clientEmail: string | null;
  clientFirstName: string;
};

/** The firm's accent, for the color input when the draft has no color yet. */
const accentSeed = (): string =>
  getComputedStyle(document.documentElement)
    .getPropertyValue("--accent-seed")
    .trim();

const SectionTitle: FC<{ children: string }> = ({ children }) => (
  <span className="text-sm font-medium text-fg-2">{children}</span>
);

/** The width the email templates are laid out at. */
const EMAIL_WIDTH = 600;

const HeaderRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex gap-2">
    <span className="w-14 shrink-0 text-sm text-fg-3">{label}</span>
    <span className="min-w-0 truncate text-fg">{value}</span>
  </div>
);

/**
 * The email exactly as the client will get it, scaled to the sheet. The
 * sandbox runs no scripts and the frame takes no clicks, so links in it stay
 * put.
 */
const EmailPreview: FC<EmailPreviewState> = ({ preview, loading, error }) => {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);
  const ready = preview !== null;

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const fit = () =>
      setScale(Math.min(1, (el.clientWidth || EMAIL_WIDTH) / EMAIL_WIDTH));
    fit();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ready]);

  if (!preview) {
    return error && !loading ? (
      <p className="text-sm text-fg-3" data-testid="delivery-email-preview">
        Couldn&apos;t load the preview.
      </p>
    ) : (
      <Skeleton className="h-64 w-full rounded-xl" />
    );
  }

  const measure = (frame: HTMLIFrameElement) => {
    const doc = frame.contentDocument;
    if (doc) setContentHeight(doc.documentElement.scrollHeight);
  };

  return (
    <div
      className="flex flex-col overflow-hidden rounded-xl ring-1 ring-line-2"
      data-testid="delivery-email-preview"
      aria-busy={loading}
    >
      <div className="flex flex-col gap-1 border-b border-line-1 bg-fill-1 px-3 py-2 text-base">
        <HeaderRow label="From" value={preview.from_name} />
        <HeaderRow label="Subject" value={preview.subject} />
      </div>
      <div
        ref={box}
        className="overflow-hidden"
        style={{ height: contentHeight ? contentHeight * scale : undefined }}
      >
        <iframe
          title="Email Preview"
          srcDoc={preview.html}
          sandbox="allow-same-origin"
          tabIndex={-1}
          onLoad={(e) => measure(e.currentTarget)}
          className="pointer-events-none block origin-top-left border-0"
          style={{
            width: EMAIL_WIDTH,
            height: contentHeight || undefined,
            transform: `scale(${scale})`
          }}
        />
      </div>
    </div>
  );
};

/**
 * "Delivery": who it goes to, how long the link lasts, reminders, the
 * welcome video and firm branding, with a preview of the email. Edits save
 * with the draft.
 */
export const DeliverySheet: FC<Props> = ({
  open,
  onOpenChange,
  draft,
  video,
  clientEmail,
  clientFirstName
}) => {
  const { settings, intake } = draft;
  const emailPreview = useEmailPreview({
    intake,
    enabled: open,
    title: draft.title,
    note: settings.attorney_note
  });
  const readOnly = !draft.editable;
  const setSchedule = (
    reminder_schedule: OutgoingIntakeReminderSchedule | null
  ) => draft.setSettings({ reminder_schedule });
  const cadence = useReminderCadence(settings.reminder_schedule, setSchedule);
  const badEmail =
    settings.recipient_email !== "" && !isEmail(settings.recipient_email);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex w-110 flex-col gap-0 data-[side=right]:sm:max-w-110"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle>Delivery</SheetTitle>
          <SheetDescription>
            How {clientFirstName || "the client"} gets this request. Saved with
            the draft.
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4">
          <FieldGroup className="gap-4">
            <Field>
              <FieldLabel htmlFor="delivery-recipient" className="text-sm">
                Send To
              </FieldLabel>
              <Input
                id="delivery-recipient"
                type="email"
                value={settings.recipient_email}
                disabled={readOnly}
                onChange={(e) =>
                  draft.setSettings({ recipient_email: e.target.value })
                }
                aria-invalid={badEmail}
              />
              <FieldDescription className="text-sm">
                {badEmail
                  ? "That doesn't look like an email address yet."
                  : clientEmail && settings.recipient_email === clientEmail
                  ? "From the client's profile."
                  : "Only this request; the profile doesn't change."}
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="delivery-note" className="text-sm">
                Note to {clientFirstName || "the client"}
              </FieldLabel>
              <Textarea
                id="delivery-note"
                value={settings.attorney_note}
                disabled={readOnly}
                rows={3}
                placeholder="Shown at the top of the form and in the email."
                onChange={(e) =>
                  draft.setSettings({ attorney_note: e.target.value })
                }
                className="min-h-0"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel className="text-sm">Link Expires After</FieldLabel>
                <Select
                  value={String(settings.expires_in_days)}
                  disabled={readOnly}
                  onValueChange={(v) =>
                    draft.setSettings({ expires_in_days: Number(v) })
                  }
                >
                  <SelectTrigger size="sm" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_DAYS.map((d) => (
                      <SelectItem key={d} value={String(d)}>
                        {d} days
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <ReminderPresetSelect
                dense={false}
                value={settings.reminder_schedule}
                presetId={cadence.presetId}
                onChoose={cadence.choose}
                disabled={readOnly}
              />
            </div>
            {cadence.custom ? (
              <CustomReminders
                dense={false}
                value={settings.reminder_schedule}
                onChange={setSchedule}
                disabled={readOnly}
              />
            ) : null}
            <p className="text-sm text-fg-3">
              Reminders reuse the same link and stop once the client submits.
            </p>
          </FieldGroup>

          <div className="flex flex-col gap-2">
            <SectionTitle>Welcome Video</SectionTitle>
            <WelcomeVideoCard
              videoUrl={video.url}
              upload={video.upload}
              who={clientFirstName || "your client"}
              readOnly={readOnly || !intake}
              onRecord={() => video.setRecording(true)}
              onUpload={(file) => video.uploadFile(file)}
              onRemove={() => void video.remove()}
              onLibrary={() => video.setLibrary(true)}
            />
          </div>

          <FieldGroup className="gap-3">
            <SectionTitle>Branding</SectionTitle>
            <Field orientation="horizontal">
              <Switch
                checked={Boolean(draft.enableFirmBranding)}
                disabled={readOnly}
                onCheckedChange={(v) =>
                  draft.setBranding({ enable_firm_branding: v })
                }
                aria-label="Show the Firm's Logo"
              />
              <div>
                <FieldLabel className="text-sm">
                  Show the Firm&apos;s Logo
                </FieldLabel>
                <FieldDescription className="text-sm">
                  The client sees your logo and colors instead of CaseBlink's.
                </FieldDescription>
              </div>
            </Field>
            {draft.enableFirmBranding ? (
              <Field>
                <FieldLabel className="text-sm">Page Color</FieldLabel>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Page Color"
                    value={draft.backgroundColor ?? accentSeed()}
                    disabled={readOnly}
                    onChange={(e) =>
                      draft.setBranding({
                        intake_background_color: e.target.value
                      })
                    }
                    className="size-8 cursor-pointer rounded-md ring-1 ring-line-2"
                  />
                  <Input
                    value={draft.backgroundColor ?? ""}
                    placeholder="From the logo"
                    disabled={readOnly}
                    onChange={(e) =>
                      draft.setBranding({
                        intake_background_color: e.target.value || null
                      })
                    }
                    className="w-32"
                  />
                  {draft.backgroundColor ? (
                    <Button
                      variant="ghost"
                      size="xs"
                      disabled={readOnly}
                      onClick={() =>
                        draft.setBranding({ intake_background_color: null })
                      }
                    >
                      Reset
                    </Button>
                  ) : null}
                </div>
              </Field>
            ) : null}
          </FieldGroup>

          <div className="flex flex-col gap-2">
            <SectionTitle>Email Preview</SectionTitle>
            <EmailPreview {...emailPreview} />
          </div>
        </div>
        <SheetFooter className="flex-row justify-end border-t border-line-1">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
